import { useEffect, useRef, useState } from 'react'
import { Bell, CheckCheck, Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui'
import { cn } from '../../lib/cn'
import { useNotificationsStore } from '../../stores/notificationsStore'
import type { NotificationItem, NotificationTab } from './types'
import { NotificationList } from './NotificationList'
import { NotificationDetailsModal } from './NotificationDetailsModal'

const tabs: Array<{ key: NotificationTab; label: string }> = [
  { key: 'actionable', label: 'Требуют действия' },
  { key: 'all', label: 'Все' },
  { key: 'archived', label: 'Архив' },
]

interface NotificationPanelProps {
  triggerClassName?: string
}

export function NotificationPanel({ triggerClassName }: NotificationPanelProps) {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null)
  const badge = useNotificationsStore((state) => state.badge)
  const items = useNotificationsStore((state) => state.items)
  const tab = useNotificationsStore((state) => state.tab)
  const loading = useNotificationsStore((state) => state.loading)
  const error = useNotificationsStore((state) => state.error)
  const setTab = useNotificationsStore((state) => state.setTab)
  const markSeen = useNotificationsStore((state) => state.markSeen)
  const markAsRead = useNotificationsStore((state) => state.markAsRead)
  const markAllAsRead = useNotificationsStore((state) => state.markAllAsRead)
  const archive = useNotificationsStore((state) => state.archive)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const openPanel = async () => {
    setOpen(true)
    void markSeen()
    const initialTab: NotificationTab = badge.actionable > 0 ? 'actionable' : 'all'
    setTab(initialTab)
    await useNotificationsStore.getState().loadNotifications(true)
    if (
      initialTab === 'actionable'
      && !useNotificationsStore.getState().items.some((item) => !item.read_at)
    ) {
      setTab('all')
      await useNotificationsStore.getState().loadNotifications(true)
    }
  }

  const selectTab = async (nextTab: NotificationTab) => {
    setTab(nextTab)
    await useNotificationsStore.getState().loadNotifications(true)
  }

  const activate = async (item: NotificationItem) => {
    setSelectedNotification(item)
    setOpen(false)
    try {
      await markAsRead(item.id)
    } catch {
      // The information remains available even if the read mutation failed.
    }
  }

  const archiveItem = async (item: NotificationItem) => {
    try {
      await archive(item.id)
    } catch {
      // The store rolls the optimistic update back.
    }
  }

  const badgeText = badge.unread > 9 ? '9+' : String(badge.unread)

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={cn(triggerClassName, 'relative')}
        onClick={() => open ? setOpen(false) : void openPanel()}
        aria-label="Уведомления"
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {badge.unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:ring-zinc-900">
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 flex w-[min(400px,calc(100vw-1rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-zinc-800">
            <h2 className="min-w-0 flex-1 text-base font-semibold text-gray-900 dark:text-zinc-100">Уведомления</h2>
            <Button
              variant="ghost"
              size="sm"
              icon={<CheckCheck className="h-4 w-4" />}
              onClick={() => void markAllAsRead()}
            >
              Прочитать всё
            </Button>
            <button
              type="button"
              className="rounded-md p-2 text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-zinc-400 dark:hover:bg-zinc-800"
              onClick={() => { setOpen(false); navigate('/settings/notifications') }}
              aria-label="Настройки уведомлений"
            >
              <Settings className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-1 overflow-x-auto border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => void selectTab(item.key)}
                className={cn(
                  'whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                  tab === item.key
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800',
                )}
              >
                {item.label}{item.key === 'actionable' && badge.actionable > 0 ? ` · ${badge.actionable}` : ''}
              </button>
            ))}
          </div>

          <div className="max-h-[560px] overflow-y-auto">
            {error && <p className="px-4 py-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
            {loading && items.length === 0 ? (
              <div className="space-y-3 p-4" aria-label="Загрузка уведомлений">
                {[1, 2, 3].map((value) => <div key={value} className="h-16 animate-pulse rounded bg-gray-100 dark:bg-zinc-800" />)}
              </div>
            ) : (
              <NotificationList
                items={items}
                compact
                onActivate={activate}
                onArchive={archiveItem}
              />
            )}
          </div>

          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/notifications') }}
            className="border-t border-gray-100 px-4 py-3 text-center text-sm font-medium text-indigo-600 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-zinc-800 dark:text-indigo-400 dark:hover:bg-indigo-950/30"
          >
            Все уведомления →
          </button>
        </div>
      )}
      <NotificationDetailsModal
        item={selectedNotification}
        onClose={() => setSelectedNotification(null)}
      />
    </div>
  )
}
